import {
  Building2,
  Car,
  ChartNoAxesCombined,
  CircleX,
  Home,
  Leaf,
  Smartphone,
  Sun,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import corporateSolarBackground from "@/res/images/buypanels-corporate.png";
import evSolarBackground from "@/res/images/buypanles-ev.png";

export type BuyPanelsLandingVariant = "evsolar" | "corporatesolar";

type Benefit = {
  title: string;
  description: string;
  icon: LucideIcon;
  overlayIcon?: LucideIcon;
};

type HeroContent = {
  heading: string;
  description: string;
  background: string;
  benefits: Benefit[];
  benefitGridClass: string;
  benefitTitleClass?: string;
  benefitDescriptionClass?: string;
};

const heroContent: Record<BuyPanelsLandingVariant, HeroContent> = {
  evsolar: {
    heading: "Start reducing your EV charging costs — at home or on the road.",
    description:
      "Choose the solar system size that suits how you charge. You own real remote solar, and the value it generates becomes AU$ credits that can help reduce your EV charging costs — whether you charge at home or use public chargers.",
    background: evSolarBackground,
    benefitGridClass: "lg:grid-cols-4",
    benefits: [
      {
        title: "Home charging",
        description:
          "Reduce your home energy bill. Your SolarCloud credits help offset the electricity bill that includes your EV charging.",
        icon: Home,
      },
      {
        title: "Public charging",
        description:
          "Help pay for charging on the road. Nominate your bank account and eligible credits can be paid there.",
        icon: Car,
      },
      {
        title: "Own real solar",
        description:
          "Professionally hosted, monitored and maintained solar in the world's premium sunlight locations.",
        icon: Sun,
      },
      {
        title: "All from your phone",
        description:
          "Buy. Monitor. Manage. Own and manage your SolarCloud from your phone wherever you charge.",
        icon: Smartphone,
      },
    ],
  },
  corporatesolar: {
    heading: "Start reducing your business energy costs with real solar — no roof required.",
    description:
      "Choose the SolarCloud capacity that suits your business. Your company owns real solar, and the value it generates becomes AU$ credits that can help reduce your Australian business energy costs.",
    background: corporateSolarBackground,
    benefitGridClass: "md:grid-cols-5",
    benefitTitleClass: "text-xs font-semibold leading-snug text-brand-orange-500",
    benefitDescriptionClass: "mt-1 text-[11px] leading-relaxed text-gray-300",
    benefits: [
      {
        title: "Business-owned solar",
        description:
          "Real SolarCloud solar owned by your business — not points or offsets.",
        icon: Building2,
      },
      {
        title: "No rooftop installation",
        description:
          "Nothing installed at your office or building. No roof access required.",
        icon: Home,
        overlayIcon: CircleX,
      },
      {
        title: "Any Australian retailer",
        description:
          "Keep your existing energy company. SolarCloud works with any Australian retailer.",
        icon: Zap,
      },
      {
        title: "Compliant for ESG and NSW Planning BASIX",
        description:
          "Supports your sustainability goals and is compliant for ESG reporting and NSW Planning BASIX.",
        icon: Leaf,
      },
      {
        title: "Moves & grows with you",
        description:
          "Move offices, add locations or increase capacity as your business grows.",
        icon: ChartNoAxesCombined,
      },
    ],
  },
};

export default function BuyPanelsVariantHero({
  variant,
}: {
  variant: BuyPanelsLandingVariant;
}) {
  const content = heroContent[variant];
  const isEvSolar = variant === "evsolar";

  return (
    <section
      aria-labelledby={`${variant}-heading`}
      className="relative isolate mb-6 overflow-hidden rounded-2xl border border-zinc-800 bg-[#0f1317] bg-cover bg-center text-white"
      style={{ backgroundImage: `url(${content.background})` }}
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0f1317]/95 via-[#0f1317]/75 to-[#0f1317]/45" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0f1317]/80 via-transparent to-[#0f1317]/10" />

      <div className="p-5 sm:p-7 lg:p-8">
        <div className="max-w-2xl">
          <h1
            id={`${variant}-heading`}
            className="text-2xl font-bold leading-tight tracking-tight sm:text-3xl lg:text-4xl"
          >
            {isEvSolar ? (
              <>
                Start reducing your
                <br />
                <span className="text-brand-orange-500">EV charging costs</span> —
                <br />
                at home or on the road.
              </>
            ) : (
              <>
                Start reducing your
                <br className="hidden sm:block" />
                <span className="text-brand-orange-500">
                  business energy costs
                </span>
                <br className="hidden sm:block" />
                {" "}with real solar — no roof required.
              </>
            )}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-200 sm:text-base">
            {content.description}
          </p>
        </div>

        <ul
          className={`mt-6 grid ${
            isEvSolar
              ? "grid-cols-2 gap-x-3 gap-y-5 sm:gap-x-5"
              : "grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 md:gap-x-5"
          } ${content.benefitGridClass}`}
        >
          {content.benefits.map(({ title, description, icon: Icon, overlayIcon: OverlayIcon }) => (
            <li
              key={title}
              className="flex flex-col items-center text-center"
            >
              <span className="relative mb-2 flex h-14 w-14 items-center justify-center rounded-full border-2 border-brand-orange-500">
                <Icon
                  aria-hidden="true"
                  className="h-7 w-7 text-brand-orange-500"
                />
                {OverlayIcon ? (
                  <OverlayIcon
                    aria-hidden="true"
                    className="absolute h-4 w-4 text-red-500"
                  />
                ) : null}
              </span>
              <div>
                <h2
                  className={
                    content.benefitTitleClass ??
                    "text-sm font-semibold leading-snug text-brand-orange-500 sm:text-base"
                  }
                >
                  {title}
                </h2>
                <p
                  className={
                    content.benefitDescriptionClass ??
                    "mt-1 text-xs leading-relaxed text-gray-300 sm:text-sm"
                  }
                >
                  {description}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
